import React, { useCallback, useRef, useState } from 'react';
import {
  ButtonNewRecord,
  FlexRow,
  TableDataState,
  TableEditButtons,
  usePaginatedQueryV8,
} from 'motion-components';
import { Card } from 'react-bootstrap';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { gqlGetQuery } from '../graphql';
import { buildWhereFromFilterValues, Filters } from './filters';
import { DataGrid } from './grid';
import { handleExport } from './export-columns';
import { useApolloClient, type ApolloClient } from '@apollo/client';
import { getRouteByName } from 'app/routes';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserLocationPermissions } from 'shared-pages/hooks';
import { observer } from 'mobx-react-lite';
import { IdocWasteDestinationTypes } from 'shared-pages/shared/constants';

/* ----------------------------------------------------------- */
/* ------------------------ Page ----------------------------- */
export const WastereceptionpointsGridPage = observer(() => {
  const { appStore, tableStore } = useRootStore();
  const { handleGraphqlError } = useHandleError('WasteReceptionPointsPage');

  const location = useLocation<{ pathname?: string; order?: string }>();
  const history = useHistory();
  const [selected, setSelected] = useState<Record<string, unknown> | null>(null);
  const { t } = useTranslation();

  const isDA = location.pathname.includes('wrecpointsda');
  const { canView, canAddNew, canEdit, canDelete } = useUserLocationPermissions();

  const handleAddNew = useCallback(() => {
    const routeName = isDA ? 'WasteReceptionPointDa' : 'WasteReceptionPoint';
    history.push({
      pathname: generatePath(getRouteByName(routeName), { id: -1 }),
      state: {
        pathname: location.pathname,
        ...location.state,
        isDA: isDA,
      },
    });
  }, [history, location.pathname, location.state]);
  /* ------------------- Query ------------------------------- */
  const initOrderBy = 'name ASC';
  const query = usePaginatedQueryV8(
    gqlGetQuery,
    buildWhereFromFilterValues,
    location.state?.order ?? initOrderBy,
    appStore,
    true,
    {
      isActive: true,
      wasteDestinationTypeId: isDA
        ? IdocWasteDestinationTypes.SecondaryStorage
        : IdocWasteDestinationTypes.Recycling,
    },
  );
  const {
    loading,
    data,
    dataCount,
    error,
    initPageIndex,
    filtersChanged,
    gridPageChanged,
    queryState,
    queryParams,
    setQueryParams,
    refetch,
  } = query;

  const client = useApolloClient() as ApolloClient<object>;

  const gridRef = useRef<unknown>(null);

  if (error) {
    handleGraphqlError('query-get', error);
    return <h3 className="text-center">{error.message}</h3>;
  } else {
    return (
      <Card className="shadow">
        <Card.Header>
          <FlexRow>
            <Card.Title className="m-0">
              {isDA ? t('Δευτερογενείς Αποθήκες') : t(' Ανακυκλωτές')}
            </Card.Title>
            {canAddNew && <ButtonNewRecord onClick={handleAddNew} size="sm" />}
          </FlexRow>
        </Card.Header>
        <Card.Body className="nopadding">
          {/* Φίλτρα */}
          <Filters
            onChange={filtersChanged}
            initialValues={queryState?.filterValues ?? {}}
            disable={tableStore.state !== TableDataState.browse}
          />

          {/* Grid */}
          <DataGrid
            ref={gridRef}
            loading={loading}
            data={data}
            dataCount={dataCount}
            initPageIndex={initPageIndex}
            onPageChange={gridPageChanged}
            handleExport={() => {
              handleExport(client, queryState, t);
            }}
            initOrderBy={queryParams?.order ?? initOrderBy}
            onOrderChange={(orderBy) => {
              const newQueryParams = { ...queryParams, order: orderBy };
              setQueryParams(newQueryParams);
              history.replace({
                pathname: location.pathname,
                state: {
                  ...location.state,
                  order: orderBy,
                },
              });
            }}
            selected={selected}
            setSelected={setSelected}
            refetch={refetch}
            isDA={isDA}
          />
        </Card.Body>
      </Card>
    );
  }
});
